import type { FieldTypeName } from '@/api/types'

/**
 * What a formula field's value behaves like.
 *
 * A formula, rollup, count or lookup field has no type of its own: the backend
 * computes one from the expression and reports it as `formula_type`. Almost
 * every question about the value -- how to sort it, how to render it, which
 * filters apply -- is then answered by whichever ordinary field type it resolves
 * to, so this map is the whole of the formula layer for those purposes.
 *
 * `array` is the exception and has no scalar equivalent: the value is a list,
 * and `array_formula_type` names what the entries are.
 */
export const FORMULA_TYPES = [
  'invalid',
  'text',
  'char',
  'button',
  'link',
  'date_interval',
  'duration',
  'date',
  'boolean',
  'number',
  'array',
  'single_select',
  'multiple_select',
  'single_file',
  'url',
  'multiple_collaborators',
] as const

export type FormulaType = (typeof FORMULA_TYPES)[number]

/** The entry type of an array formula. Every formula type except `array` itself. */
export type ArrayFormulaType = Exclude<FormulaType, 'array'>

/**
 * The field type a formula result behaves like.
 *
 * The types with no counterpart of their own -- an invalid expression, a
 * date interval, a link, a button -- all render and sort as text, which is what
 * the backend sends them as.
 */
export const FORMULA_TYPE_BEHAVES_AS: Record<ArrayFormulaType, FieldTypeName> = {
  invalid: 'text',
  text: 'text',
  char: 'text',
  button: 'text',
  link: 'text',
  date_interval: 'text',
  duration: 'duration',
  date: 'date',
  boolean: 'boolean',
  number: 'number',
  single_select: 'single_select',
  multiple_select: 'multiple_select',
  single_file: 'file',
  url: 'url',
  multiple_collaborators: 'multiple_collaborators',
}

export function isArrayFormulaType(value: unknown): value is ArrayFormulaType {
  return typeof value === 'string' && value in FORMULA_TYPE_BEHAVES_AS
}

/**
 * Whether an array formula's entries are wrapped in `{ value }`.
 *
 * Every entry type is, except a file: the backend sends a list of file objects
 * directly, and unwrapping one would reach for a `value` key it does not have.
 */
export function arrayItemIsWrapped(entryType: ArrayFormulaType): boolean {
  return entryType !== 'single_file'
}

/** Unwraps one entry of an array formula into the value its type expects. */
export function unwrapArrayItem(entryType: ArrayFormulaType, item: unknown): unknown {
  if (!arrayItemIsWrapped(entryType)) return item
  if (item === null || item === undefined || typeof item !== 'object') return item
  return (item as Record<string, unknown>)['value']
}
